import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, Plus, Settings as SettingsIcon, Building2, Check, Briefcase } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";

/**
 * Workspace switcher dropdown for the master subscriber.
 * Mounted at the top of the sidebar in DashboardShell.
 *
 * - Hidden when the user's plan does not include workspaces (free/starter/pro/enterprise).
 *   The component fetches /api/workspaces — when `max_workspaces===0` AND no workspaces
 *   exist it auto-hides so non-Fund-Manager investors don't see noise.
 * - Switching POSTs /workspaces/{id}/switch, replaces the rv_token, and reloads.
 */
export default function WorkspaceSwitcher() {
  const [meta, setMeta] = useState(null);
  const [open, setOpen] = useState(false);
  const [switching, setSwitching] = useState(false);
  const navigate = useNavigate();
  const wrapRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/workspaces");
      setMeta(res.data);
    } catch (e) {
      // silent — router 403/401 → just hide the switcher
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (wrapRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  if (!meta) return null;

  // Hide switcher entirely for plans without multi-account features
  // unless the master already has workspaces (e.g. plan was downgraded).
  if (meta.max_workspaces === 0 && (meta.items || []).length <= 1) {
    return null;
  }

  const active =
    (meta.items || []).find((it) => it.id === meta.active_workspace_id) ||
    (meta.items || [])[0];

  const switchTo = async (id) => {
    if (id === meta.active_workspace_id) {
      setOpen(false);
      return;
    }
    setSwitching(true);
    try {
      const res = await api.post(`/workspaces/${id}/switch`);
      const tok = res.data?.token;
      if (!tok) throw new Error("No token returned");
      localStorage.setItem("rv_token", tok);
      toast.success(`Switched to ${res.data.workspace.label}`);
      // Hard reload so AuthContext + every cached data fetch picks up the new scope.
      window.location.assign("/dashboard");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
      setSwitching(false);
    }
  };

  return (
    <div ref={wrapRef} className="relative px-3 pt-3" data-testid="workspace-switcher">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2 px-2.5 py-2 rounded-md border border-slate-700 bg-slate-800 hover:bg-slate-700 text-left"
        data-testid="workspace-switcher-toggle"
      >
        {meta.is_workspace_session ? (
          <Briefcase className="w-4 h-4 text-amber-300 shrink-0" />
        ) : (
          <Building2 className="w-4 h-4 text-white/70 shrink-0" />
        )}
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-widest text-white/50 font-bold leading-none">
            {meta.is_workspace_session ? "Workspace" : "Account"}
          </div>
          <div className="text-sm font-semibold text-white truncate">
            {active?.label || "—"}
          </div>
        </div>
        <ChevronDown className="w-4 h-4 text-white/40 shrink-0" />
      </button>
      {open && (
        <div
          data-testid="workspace-switcher-menu"
          className="absolute z-40 left-3 right-3 mt-1 max-h-80 overflow-y-auto bg-white border border-slate-200 rounded-md shadow-lg"
        >
          {(meta.items || []).map((it) => {
            const isActive = it.id === meta.active_workspace_id;
            return (
              <button
                key={it.id}
                onClick={() => switchTo(it.id)}
                disabled={switching}
                data-testid={`workspace-switcher-item-${it.id}`}
                className={`w-full text-left px-3 py-2.5 flex items-center gap-2 text-sm border-b border-slate-100 last:border-b-0 ${
                  isActive ? "bg-slate-50" : "hover:bg-slate-50"
                }`}
              >
                {it.is_master ? (
                  <Building2 className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                ) : (
                  <Briefcase className="w-3.5 h-3.5 text-amber-700 shrink-0" />
                )}
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-slate-900 truncate">{it.label}</div>
                  {it.company && <div className="text-xs text-slate-500 truncate">{it.company}</div>}
                </div>
                {isActive && <Check className="w-3.5 h-3.5 text-emerald-700 shrink-0" />}
              </button>
            );
          })}
          <div className="border-t border-slate-200 p-1.5 flex gap-1">
            {meta.can_create && !meta.is_workspace_session && (
              <button
                onClick={() => {
                  setOpen(false);
                  navigate("/dashboard/workspaces");
                }}
                data-testid="workspace-switcher-add"
                className="flex-1 inline-flex items-center justify-center gap-1.5 px-2 py-1.5 text-xs text-slate-700 hover:bg-slate-100 rounded"
              >
                <Plus className="w-3.5 h-3.5" /> New
              </button>
            )}
            <button
              onClick={() => {
                setOpen(false);
                navigate("/dashboard/workspaces");
              }}
              data-testid="workspace-switcher-manage"
              className="flex-1 inline-flex items-center justify-center gap-1.5 px-2 py-1.5 text-xs text-slate-700 hover:bg-slate-100 rounded"
            >
              <SettingsIcon className="w-3.5 h-3.5" /> Manage
            </button>
          </div>
        </div>
      )}
      {meta.is_workspace_session && (
        <div className="mt-2 px-2 text-[10px] uppercase tracking-widest text-amber-700 font-bold">
          Acting as workspace
        </div>
      )}
    </div>
  );
}
